import { useCallback, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import debounce from 'just-debounce-it';

import {
  MerchantOrderStatusEnum,
  type OpsOrderFilter,
} from '@/graphql/generatedTypes';
import { useSendToPickingAction } from './hooks/useSendToPickingAction';
import { useRefetchOrderDocuments } from './hooks/useRefetchOrderDocuments';
import { useRefetchDeliveryNote } from './hooks/useRefetchDeliveryNote';

import { DownloadIcon } from 'lucide-react';

import { columns, defaultHiddenColumns } from './components/table/columns';
import { OrderDataTable } from './components/table/OrderDataTable';
import type {
  FilterDefinition,
  ActiveFilter,
  ToolbarAction,
} from './components/table/OrderToolbar';
import { Routes } from '@/utils/routes';
import i18n from '@/utils/i18n';
import { toast } from 'sonner';
import {
  ORDER_SYSTEM_TO_GQL,
  type OrderSystemType,
} from './utils/orderListFilters';
import { useContainerTypes } from './utils/useContainerTypes';
import { useOrderListState } from './hooks/useOrderListState';
import { useWarehousesQuery } from '@/graphql/queries/warehouses.generated';
import { useStoreSearchAdminReducedLazyQuery } from '@/graphql/queries/storeSearchReduced.generated';
import { useOpsOrderCreatorsQuery } from '@/graphql/queries/opsOrderCreators.generated';
import { Separator } from '@/components/ui/separator';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const PAGE_SIZE = 20;

interface OrderListViewProps extends RouteComponentProps {
  baseFilter?: OpsOrderFilter;
  systemType?: OrderSystemType;
  availableFilters?: FilterDefinition[];
  defaultActiveFilters?: ActiveFilter[];
}

export const OrderListView = ({
  baseFilter,
  systemType = 'OPEN_SYSTEM',
  availableFilters: availableFiltersProp,
  defaultActiveFilters,
}: OrderListViewProps) => {
  const docTitle =
    systemType === 'EVENT'
      ? i18n.t('Event:eventOrders')
      : i18n.t('Navigation:opsOrders');
  useDocumentTitle(docTitle);

  const containerTypes = useContainerTypes();

  const list = useOrderListState({
    baseFilter: baseFilter ?? { system: 'OPEN_SYSTEM' },
    systemType,
    availableFilters: availableFiltersProp,
    defaultActiveFilters,
    defaultColumnVisibility: defaultHiddenColumns,
    pageSize: PAGE_SIZE,
  });

  const { data: warehousesData } = useWarehousesQuery();

  // Stores number in the thousands, so (unlike warehouses) there is no flat list
  // to preload — the store filter searches server-side as the user types. The
  // selected option value is a store's `external_vytal_id` (the MerchantStores
  // UUID), which `buildOrderListFilter` maps to `OpsOrderFilter.store_uuid`.
  const [searchStores, { data: storesData, loading: storesLoading }] =
    useStoreSearchAdminReducedLazyQuery({ fetchPolicy: 'no-cache' });
  const [storeQuery, setStoreQuery] = useState('');

  const debouncedStoreSearch = useMemo(
    () =>
      debounce(
        (query: string) => searchStores({ variables: { query, limit: 20 } }),
        300,
      ),
    [searchStores],
  );

  // Cancel any pending search when the filter unmounts.
  useEffect(() => () => debouncedStoreSearch.cancel(), [debouncedStoreSearch]);

  const handleStoreSearch = useCallback(
    (query: string) => {
      const q = query.trim();
      setStoreQuery(q);
      // Require at least 2 characters; below that, drop the pending search so
      // stale results from an earlier query don't linger in the dropdown.
      if (q.length < 2) {
        debouncedStoreSearch.cancel();
        return;
      }
      debouncedStoreSearch(q);
    },
    [debouncedStoreSearch],
  );

  const storeOptions = useMemo(() => {
    if (storeQuery.length < 2) return [];
    return (storesData?.storeSearchAdmin ?? [])
      .map((result) => result.store)
      .filter(
        (store): store is NonNullable<typeof store> =>
          store != null && store.external_vytal_id != null,
      )
      .map((store) => ({
        value: store.external_vytal_id as string,
        getLabel: () => store.name ?? (store.external_vytal_id as string),
      }));
  }, [storesData, storeQuery]);

  // Only users who actually created an order in this system show up, so the
  // dropdown can never offer a creator the filter would find nothing for.
  const { data: creatorsData, loading: creatorsLoading } =
    useOpsOrderCreatorsQuery({
      variables: { system: ORDER_SYSTEM_TO_GQL[systemType] },
    });

  const creatorOptions = useMemo(
    () =>
      (creatorsData?.opsOrderCreators ?? []).map((creator) => ({
        value: creator.id,
        getLabel: () => creator.name,
      })),
    [creatorsData],
  );

  const availableFilters = (() => {
    const warehouseOptions = (warehousesData?.warehouses ?? []).map((w) => ({
      value: w.id,
      getLabel: () => w.name ?? w.text_id,
    }));
    return list.availableFilters.map((def): FilterDefinition => {
      if (def.id === 'warehouse') return { ...def, options: warehouseOptions };
      if (def.id === 'createdBy')
        return { ...def, options: creatorOptions, loading: creatorsLoading };
      if (def.id === 'store')
        return {
          ...def,
          options: storeOptions,
          onSearch: handleStoreSearch,
          loading: storesLoading,
          emptyText: i18n.t('Order:filterStoreHint'),
        };
      return def;
    });
  })();

  const { handleRefetchOrderDocuments } = useRefetchOrderDocuments();
  const { handleRefetchDeliveryNote } = useRefetchDeliveryNote();

  const { handleSendToPicking } = useSendToPickingAction({
    filter: list.filter,
    onSuccess: async () => {
      toast.success(i18n.t('Order:toastSendToPickingSuccess'));
      await list.refetch();
    },
    onPartialError: () => {
      toast.error(i18n.t('Order:toastSendToPickingPartialError'));
      list.setActiveFilters([
        { id: 'status', value: MerchantOrderStatusEnum.Error },
      ]);
    },
  });

  const getRowHref = (order: { id: string }) => Routes.orderDetails(order.id);

  const primaryAction: ToolbarAction = {
    label: i18n.t('Order:sendToPicking'),
    onClick: handleSendToPicking,
    requiredStatus: MerchantOrderStatusEnum.Approved,
  };

  const overflowActions: ToolbarAction[] = [
    {
      label: i18n.t('Order:exportSelected'),
      icon: <DownloadIcon />,
      onClick: list.handleExport,
    },
  ];

  return (
    <OrderDataTable
      columns={columns}
      columnVisibility={list.columnVisibility}
      onColumnVisibilityChange={list.setColumnVisibility}
      data={list.orders}
      totalCount={list.totalCount}
      currentPage={list.currentPage}
      pageSize={PAGE_SIZE}
      header={
        <>
          <div className="tw:flex tw:flex-col tw:gap-2">
            <h1 className="tw:text-4xl tw:font-semibold">
              {i18n.t('Navigation:opsOrders')}
            </h1>
            <p className="tw:leading-6">
              {i18n.t('Order:opsOrdersPageDescription')}
            </p>
          </div>

          <Separator />
        </>
      }
      onPageChange={list.setCurrentPage}
      isLoading={list.loading}
      isError={list.error != null}
      sorting={list.sorting}
      onSortingChange={list.setSorting}
      containerTypes={containerTypes}
      selectedContainerTypes={list.selectedContainerTypes}
      onContainerTypeChange={list.handleContainerTypeChange}
      availableFilters={availableFilters}
      activeFilters={list.activeFilters}
      onFilterChange={list.handleFilterChange}
      onFilterAdd={list.handleFilterAdd}
      onFilterRemove={list.handleFilterRemove}
      getRowHref={getRowHref}
      primaryAction={primaryAction}
      overflowActions={overflowActions}
      onCancel={list.handleCancel}
      onUpdateStatus={list.handleUpdateStatus}
      onRefetchOrderDocuments={handleRefetchOrderDocuments}
      onRefetchDeliveryNote={handleRefetchDeliveryNote}
    />
  );
};
